import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Minus, Plus, Search, Star, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import { ServiceItemImage } from '@/components/shared/ServiceItemPicker'
import { useActiveServiceCategories } from '@/hooks/useServiceCategoryOptions'
import { useInfiniteServiceItemCatalog } from '@/hooks/useSettings'
import { useDebounce } from '@/hooks/useDebounce'
import { formatCurrency } from '@/lib/format'
import {
  SERVICE_CATEGORY_ORDER,
  getServiceCategoryPalette,
  type ServiceCategoryPalette,
} from '@/lib/serviceCategoryPalette'
import {
  getFavoriteServiceItemIds,
  toggleFavoriteServiceItemId,
} from '@/lib/serviceItemFavorites'
import { cn } from '@/lib/utils'
import type { ServiceCategory, ServiceItem } from '@/types'

export interface SelectedServiceItem {
  item: ServiceItem
  quantity: number
}

interface Props {
  onClose: () => void
  onPick: (items: SelectedServiceItem[]) => void
  destination?: 'quote' | 'invoice'
}

interface CategoryGroup {
  id: string
  name: string
  items: ServiceItem[]
  palette: ServiceCategoryPalette
}

const DRAWER_WIDTH = 'w-full max-w-[640px]'
const drawerClass = 'tf-right-drawer-short [&>button]:hidden'

function compareCategoryNames(a: string, b: string) {
  const aIndex = SERVICE_CATEGORY_ORDER.indexOf(a)
  const bIndex = SERVICE_CATEGORY_ORDER.indexOf(b)

  if (aIndex === -1 && bIndex === -1) return a.localeCompare(b)
  if (aIndex === -1) return 1
  if (bIndex === -1) return -1
  return aIndex - bIndex
}

function resolveCategoryName(item: ServiceItem, fallback: string) {
  return item.category?.name ?? item.service_category?.name ?? fallback
}

function buildGroups(items: ServiceItem[], fallback: string) {
  const groups = new Map<string, CategoryGroup>()

  items.forEach(item => {
    const name = resolveCategoryName(item, fallback)
    const id = item.category?.id ?? item.service_category_id ?? name

    if (!groups.has(id)) {
      groups.set(id, {
        id,
        name,
        items: [],
        palette: getServiceCategoryPalette(name),
      })
    }

    groups.get(id)?.items.push(item)
  })

  return Array.from(groups.values()).sort((a, b) =>
    compareCategoryNames(a.name, b.name)
  )
}

function computeItemInclTax(item: ServiceItem, quantity: number) {
  const subtotal = item.unit_price * quantity
  const rate = item.tax_rate ?? 0
  return subtotal * (1 + rate / 100)
}

export function ServiceItemLibraryPage({
  onClose,
  onPick,
  destination = 'quote',
}: Props) {
  const { t } = useTranslation('billing')
  const [search, setSearch] = useState('')
  const [activeCategoryId, setActiveCategoryId] = useState<string>('all')
  const [selected, setSelected] = useState<Record<string, SelectedServiceItem>>(
    {}
  )
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() =>
    getFavoriteServiceItemIds()
  )
  const debouncedSearch = useDebounce(search.trim(), 300)
  const searchIsSettling = search.trim() !== debouncedSearch
  const scrollRef = useRef<HTMLDivElement>(null)
  const selectedCategoryId =
    activeCategoryId === 'all' ? undefined : activeCategoryId

  const { data: categoriesData } = useActiveServiceCategories()
  const categoriesReady = categoriesData !== undefined

  const {
    data,
    isLoading,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteServiceItemCatalog(debouncedSearch, selectedCategoryId)

  const categories = useMemo<ServiceCategory[]>(
    () =>
      [...(categoriesData ?? [])].sort((a, b) =>
        compareCategoryNames(a.name, b.name)
      ),
    [categoriesData]
  )

  const allItems = useMemo(
    () =>
      (data?.pages.flatMap(page => page.data) ?? []).filter(
        item => item.is_active
      ),
    [data]
  )

  const allGroups = useMemo(
    () => buildGroups(allItems, t('serviceItemDialog.uncategorised')),
    [allItems, t]
  )

  const loadNextPage = () => {
    if (!hasNextPage || isFetchingNextPage || searchIsSettling) return
    void fetchNextPage()
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [activeCategoryId, debouncedSearch])

  useEffect(() => {
    const list = scrollRef.current
    if (!list || !hasNextPage || isFetchingNextPage || searchIsSettling) {
      return
    }

    // Continue paging until the drawer is scrollable or the server has no more
    // pages.
    if (list.scrollHeight <= list.clientHeight + 120) {
      void fetchNextPage()
    }
  }, [
    activeCategoryId,
    allItems.length,
    debouncedSearch,
    fetchNextPage,
    allGroups.length,
    hasNextPage,
    isFetchingNextPage,
    searchIsSettling,
  ])

  const loading = isLoading || searchIsSettling

  const selectedItems = useMemo(() => Object.values(selected), [selected])
  const selectedCount = selectedItems.length
  const totalExclTax = useMemo(
    () =>
      selectedItems.reduce(
        (sum, selection) =>
          sum + selection.item.unit_price * selection.quantity,
        0
      ),
    [selectedItems]
  )
  const totalInclTax = useMemo(
    () =>
      selectedItems.reduce(
        (sum, selection) =>
          sum + computeItemInclTax(selection.item, selection.quantity),
        0
      ),
    [selectedItems]
  )

  const toggleItem = (item: ServiceItem) => {
    setSelected(current => {
      if (current[item.id]) {
        const next = { ...current }
        delete next[item.id]
        return next
      }

      return {
        ...current,
        [item.id]: { item, quantity: 1 },
      }
    })
  }

  const setQuantity = (item: ServiceItem, nextQuantity: number) => {
    setSelected(current => {
      if (!current[item.id]) {
        return {
          ...current,
          [item.id]: { item, quantity: Math.max(1, nextQuantity) },
        }
      }

      return {
        ...current,
        [item.id]: {
          item,
          quantity: Math.max(1, nextQuantity),
        },
      }
    })
  }

  const handleConfirm = () => {
    if (selectedCount === 0) return
    onPick(selectedItems)
    setSelected({})
    setSearch('')
    setActiveCategoryId('all')
  }

  const toggleFavorite = (id: string) => {
    setFavoriteIds(toggleFavoriteServiceItemId(id))
  }

  return (
    <Dialog open={categoriesReady} onOpenChange={open => !open && onClose()}>
      <DialogContent
        side='right'
        overlayClassName='bg-black/0'
        className={cn(drawerClass, DRAWER_WIDTH)}
      >
        <div className='flex h-full min-h-0 flex-col bg-white'>
          <header className='tf-right-drawer-header tf-right-drawer-header-accent'>
            <DialogTitle className='min-w-0 flex-1 font-heading text-base font-semibold leading-[1.2] tracking-normal text-[#171a20]'>
              {t('serviceItemDialog.libraryTitle')}
            </DialogTitle>
            <button
              type='button'
              aria-label={t('serviceItemDialog.close')}
              className='inline-flex size-9 items-center justify-center rounded-[12px] bg-[#f5f6fa] text-[#272c36] transition-colors hover:bg-[#eceef3]'
              onClick={onClose}
            >
              <X className='size-4' />
            </button>
          </header>

          <div className='border-b border-[#e7e9ef] bg-white px-6 py-4 shadow-[0_1px_4px_rgba(38,45,66,0.08),0_1px_4px_rgba(38,45,66,0.08)]'>
            <div className='relative'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#272c36]' />
              <Input
                value={search}
                onChange={event => setSearch(event.target.value)}
                maxLength={100}
                placeholder={t('serviceItemDialog.searchPlaceholder')}
                className='h-10 rounded-[12px] border-[#e7e9ef] bg-[#f5f6fa] pl-10 text-sm font-normal leading-[1.4] text-[#3d434e] shadow-none placeholder:text-[#3d434e]'
                aria-busy={loading}
              />
            </div>

            <div className='mt-4 flex flex-wrap gap-2'>
              {categories.map(category => {
                const isActive = activeCategoryId === category.id

                return (
                  <button
                    key={category.id}
                    type='button'
                    className={cn(
                      'inline-flex h-7 items-center gap-2 rounded-full bg-[#f5f6fa] px-2 text-xs font-medium leading-4 text-[#171a20] transition-colors',
                      isActive &&
                        'bg-[#eceef3] shadow-[inset_0_0_0_1px_#171a20]'
                    )}
                    onClick={() =>
                      setActiveCategoryId(current =>
                        current === category.id ? 'all' : category.id
                      )
                    }
                  >
                    <ServiceCategoryIcon
                      name={category.name}
                      className='size-4'
                    />
                    <span>{category.name}</span>
                    <span className='inline-flex size-4 items-center justify-center rounded-full bg-[#8f95a6] text-[10px] font-medium leading-4 text-white'>
                      {category.service_items_count ?? 0}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div
            ref={scrollRef}
            data-testid='service-item-library-scroll'
            className='min-h-0 flex-1 overflow-y-auto py-6 pl-6 pr-[7px]'
            onScroll={event => {
              const list = event.currentTarget
              const remaining =
                list.scrollHeight - list.scrollTop - list.clientHeight
              if (remaining <= 120) loadNextPage()
            }}
          >
            {loading ? (
              <p className='py-12 text-center text-[16px] font-medium leading-[1.4] text-[#6c7285]'>
                {t('serviceItemDialog.loading')}
              </p>
            ) : isError && allItems.length === 0 ? (
              <div className='flex flex-col items-center gap-3 py-12 text-center'>
                <p className='text-[16px] font-medium leading-[1.4] text-destructive'>
                  {t('serviceItemDialog.error')}
                </p>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => void refetch()}
                >
                  {t('serviceItemDialog.retry')}
                </Button>
              </div>
            ) : allGroups.length === 0 ? (
              <p className='py-12 text-center text-[16px] font-medium leading-[1.4] text-[#6c7285]'>
                {t('serviceItemDialog.empty')}
              </p>
            ) : (
              <div className='space-y-6'>
                {allGroups.map(group => (
                  <section
                    key={group.id}
                    className='w-[592px] max-w-full space-y-3'
                  >
                    <div
                      className='flex h-9 w-[592px] max-w-full items-center gap-2 rounded-[12px] px-3 py-2'
                      style={{ backgroundColor: group.palette.headerBg }}
                    >
                      <ServiceCategoryIcon
                        name={group.name}
                        className='size-4'
                      />
                      <span
                        className='truncate text-xs font-medium uppercase leading-4'
                        style={{ color: group.palette.headerText }}
                      >
                        {group.name}
                      </span>
                      <span className='inline-flex size-4 items-center justify-center rounded-full bg-[#8f95a6] text-[10px] font-medium leading-4 text-white'>
                        {group.items.length}
                      </span>
                    </div>

                    <div className='space-y-3'>
                      {group.items.map(item => {
                        const selection = selected[item.id]
                        const isSelected = !!selection
                        const isFavorite = favoriteIds.includes(item.id)

                        return (
                          <div
                            key={item.id}
                            className={cn(
                              'relative h-[92px] w-[592px] max-w-full rounded-[16px] border p-3 text-left transition-colors',
                              isSelected
                                ? 'border-[#059669] bg-[#ecfdf5] shadow-[0_1px_4px_-1px_#ff5a1f,0_1px_8px_-1px_#ffe2d2]'
                                : 'border-[#e7e9ef] bg-white hover:border-[#ff8b66] hover:bg-[#fffaf7]'
                            )}
                          >
                            <button
                              type='button'
                              aria-label={item.name}
                              aria-pressed={isSelected}
                              className='absolute inset-0 z-0 rounded-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff8b66] focus-visible:ring-offset-2'
                              onClick={() => toggleItem(item)}
                            />
                            {isSelected ? (
                              <span className='absolute left-2 top-2 inline-flex h-6 min-w-[25px] items-center justify-center rounded-[8px] bg-[#10b981] px-1.5 text-[12px] font-medium leading-4 text-white z-11'>
                                x{selection.quantity}
                              </span>
                            ) : null}

                            <div className='pointer-events-none relative z-10 flex items-center gap-3'>
                              <ServiceItemImage
                                src={item.image_url}
                                className='h-16 w-16 rounded-[12px] bg-[#f4f5f8]'
                                loading='lazy'
                              />

                              <div className='min-w-0 flex-1'>
                                <p className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
                                  {item.name}
                                </p>
                                {item.description ? (
                                  <p className='mt-1 line-clamp-1 text-xs font-medium leading-4 text-[#6c7285]'>
                                    {item.description}
                                  </p>
                                ) : null}
                                <div className='mt-2 flex items-end gap-2'>
                                  <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                                    {formatCurrency(item.unit_price, 'EUR', 2)}
                                  </span>
                                  {item.unit ? (
                                    <span className='text-xs font-medium leading-4 text-[#6c7285]'>
                                      /{item.unit}
                                    </span>
                                  ) : null}
                                </div>
                              </div>

                              <div className='pointer-events-auto flex shrink-0 items-center gap-3 self-center'>
                                {isSelected ? (
                                  <div
                                    className='grid h-9 w-[104px] grid-cols-[16px_1fr_16px] items-center rounded-[12px] border border-[#e7e9ef] bg-white px-3 shadow-[0_1px_4px_rgba(38,45,66,0.05)]'
                                    onClick={event => event.stopPropagation()}
                                  >
                                    <button
                                      type='button'
                                      className='inline-flex size-4 items-center justify-center text-[#272c36]'
                                      onClick={() =>
                                        setQuantity(
                                          item,
                                          selection.quantity - 1
                                        )
                                      }
                                    >
                                      <Minus className='size-4' />
                                    </button>
                                    <span className='text-center text-sm font-medium leading-[1.4] text-[#171a20]'>
                                      {selection.quantity}
                                    </span>
                                    <button
                                      type='button'
                                      className='inline-flex size-4 items-center justify-center text-[#272c36]'
                                      onClick={() =>
                                        setQuantity(
                                          item,
                                          selection.quantity + 1
                                        )
                                      }
                                    >
                                      <Plus className='size-4' />
                                    </button>
                                  </div>
                                ) : null}

                                <button
                                  type='button'
                                  aria-label={t(
                                    'serviceItemDialog.toggleFavorite'
                                  )}
                                  className={cn(
                                    'inline-flex size-6 items-center justify-center rounded-[8px] bg-[#f4f5f8] transition-colors hover:bg-[#eceef3]',
                                    isFavorite
                                      ? 'text-[#f5c650] hover:text-[#d89d00]'
                                      : 'text-[#272c36] hover:text-[#171a20]'
                                  )}
                                  onClick={event => {
                                    event.stopPropagation()
                                    toggleFavorite(item.id)
                                  }}
                                >
                                  <Star
                                    className={cn(
                                      'size-4',
                                      isFavorite && 'fill-current'
                                    )}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </section>
                ))}

                {isFetchingNextPage ? (
                  <div className='flex items-center justify-center gap-2 py-3 text-[14px] font-medium text-[#6c7285]'>
                    <Loader2 className='size-4 animate-spin' />
                    {t('serviceItemDialog.loadingMore')}
                  </div>
                ) : null}

                {isError && allItems.length > 0 ? (
                  <div className='flex flex-col items-center gap-2 py-3 text-center'>
                    <p className='text-[14px] font-medium text-destructive'>
                      {t('serviceItemDialog.error')}
                    </p>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() =>
                        void (isFetchNextPageError
                          ? fetchNextPage()
                          : refetch())
                      }
                    >
                      {t('serviceItemDialog.retry')}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <footer className='box-border h-[72px] border-t border-[#d9e2ec] bg-white px-6'>
            <div className='flex h-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
              <div className='min-w-0 text-sm font-medium leading-[1.4] text-[#171a20]'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span>
                    {t('serviceItemDialog.selected', { count: selectedCount })}
                  </span>
                  <span className='size-1 rounded-full bg-[#8f95a6]' />
                  <span>
                    {t('serviceItemDialog.totalExclTax', {
                      amount: formatCurrency(totalExclTax, 'EUR', 2),
                    })}
                  </span>
                  <span className='size-1 rounded-full bg-[#8f95a6]' />
                  <span className='text-[#047857]'>
                    {t('serviceItemDialog.totalInclTax', {
                      amount: formatCurrency(totalInclTax, 'EUR', 2),
                    })}
                  </span>
                </div>
              </div>

              <Button
                type='button'
                className='h-10 rounded-[12px] border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'
                disabled={selectedCount === 0}
                onClick={handleConfirm}
              >
                {t(
                  destination === 'invoice'
                    ? 'serviceItemDialog.addToInvoice'
                    : 'serviceItemDialog.addToQuote'
                )}
              </Button>
            </div>
          </footer>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ServiceItemLibraryPage
